// src/components/Propuestas/ListadoPropuestas.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Table, Button, Badge, Form, Row, Col, 
  Spinner, Alert
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { eliminarPropuesta } from '../../api/common';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../commons/PaginationControls';
import ClientSearchSelect from '../../utils/ClientSearchSelect';

const ListadoPropuestas = () => {
  const navigate = useNavigate();
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [consecutivoInput, setConsecutivoInput] = useState('');
  const consecutivoTimeoutRef = useRef(null);

  const {
    results,
    pagination,
    loading,
    error,
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    changePage,
    changePerPage,
    refresh,
    pendingUpdates,
  } = usePaginatedList({
    endpoint: '/Propuestas/paginar',
    initialPerPage: 20,
    sort: { fecha_propuesta: -1 },
    buildConditions: (f) => {
      const cond = { version_actual: true, activo: true };
      if (f.estado)      cond.estado      = f.estado;
      if (f.cliente_id)  cond.cliente_id  = f.cliente_id;
      if (f.consecutivo) cond.consecutivo = { $regex: f.consecutivo, $options: 'i' };
      if (f.fecha_desde || f.fecha_hasta) {
        cond.fecha_propuesta = {};
        if (f.fecha_desde) cond.fecha_propuesta.$gte = new Date(f.fecha_desde);
        if (f.fecha_hasta) cond.fecha_propuesta.$lte = new Date(f.fecha_hasta + 'T23:59:59');
      }
      return cond;
    }
  });

  // Sincroniza el input cuando el filtro se limpia/cambia desde afuera (ej. "Limpiar")
  useEffect(() => {
    setConsecutivoInput(filters.consecutivo || '');
  }, [filters.consecutivo]);

  useEffect(() => () => clearTimeout(consecutivoTimeoutRef.current), []);

  // Debounce 300ms: evita disparar una petición por cada tecla escrita
  const handleConsecutivoChange = (e) => {
    const value = e.target.value;
    setConsecutivoInput(value);
    clearTimeout(consecutivoTimeoutRef.current);
    consecutivoTimeoutRef.current = setTimeout(() => {
      updateFilter('consecutivo', value);
    }, 300);
  };

  const handleEliminar = async (id, consecutivo) => {
    if (!window.confirm(`¿Está seguro de eliminar la propuesta ${consecutivo || id}?`)) return;
    try {
      const response = await eliminarPropuesta(id);
      if (response.ok) {
        setMensaje({ tipo: 'success', texto: 'Propuesta eliminada correctamente' });
        refresh();
      } else {
        setMensaje({ tipo: 'danger', texto: response.message || 'Error al eliminar' });
      }
    } catch {
      setMensaje({ tipo: 'danger', texto: 'Error al eliminar propuesta' });
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      borrador:           { variant: 'secondary', icon: 'pencil',        text: 'Borrador' },
      autorizada:         { variant: 'info',      icon: 'check2-circle', text: 'Autorizada' },
      enviada:            { variant: 'primary',   icon: 'send',          text: 'Enviada' },
      vista:              { variant: 'primary',   icon: 'eye',           text: 'Vista' },
      aprobada:           { variant: 'success',   icon: 'check-circle',  text: 'Aprobada' },
      rechazada:          { variant: 'danger',    icon: 'x-circle',      text: 'Rechazada' },
      cambios_solicitados:{ variant: 'warning',   icon: 'pencil-square', text: 'Cambios Sol.' },
      vencida:            { variant: 'warning',   icon: 'clock',         text: 'Vencida' },
      facturada:          { variant: 'dark',      icon: 'receipt',       text: 'Facturada' },
    };
    const est = estados[estado] || estados.borrador;
    return (
      <Badge bg={est.variant}>
        <i className={`bi bi-${est.icon} me-1`}></i>
        {est.text}
      </Badge>
    );
  };

  return (
    <div className="container-fluid py-4">
      <Card className="w-100">
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center w-100">
          <h5 className="mb-0">
            <i className="bi bi-file-earmark-text me-2"></i>
            Propuestas Comerciales
          </h5>
          <Button variant="light" size="sm" onClick={() => navigate('/propuestas/crear')}>
            <i className="bi bi-plus-circle me-2"></i>
            Nueva Propuesta
          </Button>
        </Card.Header>

        <Card.Body className="w-100">

          {/* FILTROS */}
          <Card className="mb-3 border-0 shadow-sm w-100">
            <Card.Body className="bg-light w-100">
              <Row className="align-items-end g-2">
                <Col md={2}>
                  <Form.Label className="small fw-bold mb-1">Consecutivo</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Buscar por consecutivo..."
                    value={consecutivoInput}
                    onChange={handleConsecutivoChange}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label className="small fw-bold mb-1">Estado</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.estado || ''}
                    onChange={(e) => updateFilter('estado', e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="borrador">Borrador</option>
                    <option value="autorizada">Autorizada</option>
                    <option value="enviada">Enviada</option>
                    <option value="vista">Vista</option>
                    <option value="aprobada">Aprobada</option>
                    <option value="rechazada">Rechazada</option>
                    <option value="cambios_solicitados">Cambios Solicitados</option>
                    <option value="vencida">Vencida</option>
                    <option value="facturada">Facturada</option>
                  </Form.Select>
                </Col>

                <Col md={2}>
                  <Form.Label className="small fw-bold mb-1">Cliente</Form.Label>
                  <ClientSearchSelect
                    value={filters._clienteOption || null}
                    onChange={(opt) => updateFilters({
                      ...filters,
                      cliente_id: opt?.value || '',
                      _clienteOption: opt || null,
                    })}
                    placeholder="Buscar cliente..."
                    menuPortalTarget={document.body}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label className="small fw-bold mb-1">Desde</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={filters.fecha_desde || ''}
                    onChange={(e) => updateFilter('fecha_desde', e.target.value)}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label className="small fw-bold mb-1">Hasta</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={filters.fecha_hasta || ''}
                    onChange={(e) => updateFilter('fecha_hasta', e.target.value)}
                  />
                </Col>

                <Col md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    onClick={resetFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>Limpiar
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Mensaje local (eliminar, etc.) */}
          {mensaje.texto && (
            <Alert
              variant={mensaje.tipo}
              dismissible
              onClose={() => setMensaje({ tipo: '', texto: '' })}
            >
              {mensaje.texto}
            </Alert>
          )}

          {/* Error de carga */}
          {error && (
            <Alert variant="danger" className="d-flex align-items-center justify-content-between">
              <span><strong>Error al cargar datos:</strong> {error}</span>
              <Button variant="outline-danger" size="sm" onClick={refresh}>Reintentar</Button>
            </Alert>
          )}

          {/* TABLA */}
          <div className="table-responsive">
            <Table hover size="sm">
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Vigencia</th>
                  <th>Estado</th>
                  <th className="text-center">Sync</th>
                  <th className="text-end">Total</th>
                  <th>Ver.</th>
                  <th>Creado/Editado por</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" />Cargando...
                    </td>
                  </tr>
                ) : results.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-4">
                      <Alert variant="info" className="mb-0 d-inline-flex flex-column align-items-center">
                        <i className="bi bi-inbox fs-1 d-block mb-2" />
                        <p className="mb-2">No se encontraron propuestas con los filtros aplicados.</p>
                        <Button variant="outline-info" size="sm" onClick={resetFilters}>Limpiar filtros</Button>
                      </Alert>
                    </td>
                  </tr>
                ) : results.map((prop) => (
                  <tr key={prop._id}>
                    <td>
                      <strong>{prop.consecutivo || <span className="text-muted fst-italic">Sin nro.</span>}</strong>
                    </td>
                    <td>
                      <strong>{prop.cliente_nombre}</strong>
                      <br />
                      <small className="text-muted">{prop.cliente_nit}</small>
                    </td>
                    <td>{moment.utc(prop.fecha_propuesta).format('DD/MM/YY')}</td>
                    <td>
                      <span className={moment.utc(prop.fecha_vigencia).isBefore(moment()) ? 'text-danger' : ''}>
                        {moment.utc(prop.fecha_vigencia).format('DD/MM/YY')}
                      </span>
                    </td>
                    <td>{getEstadoBadge(prop.estado)}</td>
                    <td className="text-center">
                      {prop.sincronizada
                        ? <i className="bi bi-check-circle-fill text-success" title={`Nº contable: ${prop.propuestaContable_numero || '—'}`}></i>
                        : <i className="bi bi-circle text-muted" title="No sincronizada"></i>
                      }
                    </td>
                    <td className="text-end fw-bold">
                      ${prop.total_pagar?.toLocaleString('es-CO')}
                    </td>
                    <td>
                      <Badge bg="secondary">v{prop.numero_version}</Badge>
                    </td>
                    <td>
                      {prop.creado_por
                          ? <><small className="text-muted">Creado:</small><br /><strong>{prop.creado_por.nombre} {prop.creado_por.apellido}</strong></>
                          : <span className="text-muted">—</span>
                      }
                    </td>
                    <td className="text-center">
                      <div className="btn-group btn-group-sm">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigate(`/propuestas/ver/${prop._id}`)}
                          title="Ver detalle"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => navigate(`/propuestas/editar/${prop._id}`)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleEliminar(prop._id, prop.consecutivo)}
                          title="Eliminar"
                          disabled={prop.estado !== 'borrador'}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* PAGINACIÓN ESTÁNDAR */}
          <PaginationControls
            {...pagination}
            onPageChange={changePage}
            onPerPageChange={changePerPage}
            onRefresh={refresh}
            loading={loading}
            pendingUpdates={pendingUpdates}
          />

        </Card.Body>
      </Card>
    </div>
  );
};

export default ListadoPropuestas;
